import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, FileText, CalendarDays, TrendingUp, RefreshCw, Trash2, Mail, Phone, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { api } from "../lib/api";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_COLOR = {
  new: "bg-blue-100 text-[#1E3A6B] border-blue-200",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  qualified: "bg-violet-100 text-violet-800 border-violet-200",
  won: "bg-[#E6F2EB] text-[#236944] border-[#BFDDC9]",
  lost: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminDashboard() {
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("gis_admin_token");
  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }
  return <AdminDashboardInner />;
}

function AdminDashboardInner() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const email = localStorage.getItem("gis_admin_email") || "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads", { params: tab === "all" ? {} : { type: tab } }),
      ]);
      setStats(s.data);
      setLeads(l.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("gis_admin_token");
        nav("/admin/login");
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, [tab, nav]);

  useEffect(() => {
    if (!localStorage.getItem("gis_admin_token")) {
      nav("/admin/login");
      return;
    }
    load();
  }, [load, nav]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("gis_admin_token");
    localStorage.removeItem("gis_admin_email");
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F8]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Greenville Integrated Solutions"
              className="h-14 w-auto object-contain"
            />
            <span className="hidden sm:inline text-xs font-mono uppercase tracking-[0.2em] text-slate-500 border-l border-slate-300 pl-3">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline" data-testid="admin-user-email">{email}</span>
            <Button variant="outline" size="sm" onClick={load} className="rounded-none" data-testid="admin-refresh-btn">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </Button>
            <Button variant="outline" size="sm" onClick={logout} className="rounded-none" data-testid="admin-logout-btn">
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-4 md:px-8 py-8">
        <h1 className="font-display font-bold text-3xl text-slate-900">Operations</h1>
        <p className="text-sm text-slate-500 mt-1">Lead pipeline, quote requests, and scheduled assessments.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="admin-stats">
          <Stat label="Total leads" value={stats?.total ?? "—"} icon={Inbox} />
          <Stat label="New / unread" value={stats?.new ?? "—"} icon={TrendingUp} accent />
          <Stat label="Quote requests" value={stats?.quotes ?? "—"} icon={FileText} />
          <Stat label="Assessments" value={stats?.assessments ?? "—"} icon={CalendarDays} />
        </div>

        <div className="mt-8 bg-white border border-slate-200">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-slate-50 border-b border-slate-200 rounded-none w-full justify-start h-11 p-0">
              <TabsTrigger data-testid="tab-all" value="all" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#1E3A6B] px-5 h-full">All</TabsTrigger>
              <TabsTrigger data-testid="tab-contact" value="contact" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#1E3A6B] px-5 h-full">Contact</TabsTrigger>
              <TabsTrigger data-testid="tab-quote" value="quote" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#1E3A6B] px-5 h-full">Quotes</TabsTrigger>
              <TabsTrigger data-testid="tab-assessment" value="assessment" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#1E3A6B] px-5 h-full">Assessments</TabsTrigger>
            </TabsList>

            <TabsContent value={tab} className="p-0 m-0">
              <LeadList leads={leads} onUpdate={updateStatus} onDelete={remove} loading={loading} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className={`bg-white border p-5 ${accent ? "border-[#1E3A6B]" : "border-slate-200"}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
          <div className="font-display font-black text-3xl text-slate-900 mt-2">{value}</div>
        </div>
        <Icon size={18} className={accent ? "text-[#1E3A6B]" : "text-slate-400"} />
      </div>
    </div>
  );
}

function LeadList({ leads, onUpdate, onDelete, loading }) {
  if (loading && leads.length === 0) return <div className="p-10 text-center text-sm text-slate-500">Loading…</div>;
  if (!loading && leads.length === 0)
    return <div className="p-12 text-center" data-testid="no-leads"><Inbox className="mx-auto text-slate-300" size={36} /><div className="text-sm text-slate-500 mt-3">No leads yet.</div></div>;

  return (
    <div className="divide-y divide-slate-200" data-testid="leads-list">
      {leads.map((l) => (
        <div key={l.id} data-testid={`lead-row-${l.id}`} className="p-5 hover:bg-slate-50 transition-colors grid grid-cols-12 gap-4 items-center">
          <div className="col-span-12 md:col-span-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="rounded-none border-slate-300 text-[10px] uppercase font-mono">{l.type}</Badge>
              <Badge className={`rounded-none border ${STATUS_COLOR[l.status] || ""}`}>{l.status}</Badge>
            </div>
            <div className="font-display font-semibold text-slate-900 mt-2">{l.name}</div>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
              <span className="inline-flex items-center gap-1"><Mail size={11} /> {l.email}</span>
              <span className="inline-flex items-center gap-1"><Phone size={11} /> {l.phone}</span>
              {l.company && <span className="inline-flex items-center gap-1"><Building2 size={11} /> {l.company}</span>}
            </div>
          </div>

          <div className="col-span-12 md:col-span-4 text-sm text-slate-600">
            <Summary lead={l} />
          </div>

          <div className="col-span-12 md:col-span-2 text-xs text-slate-500 font-mono">
            {new Date(l.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
          </div>

          <div className="col-span-12 md:col-span-2 flex items-center gap-2 justify-end">
            <Select value={l.status} onValueChange={(v) => onUpdate(l.id, v)}>
              <SelectTrigger data-testid={`status-select-${l.id}`} className="h-9 rounded-none border-slate-300 text-xs w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-none h-9" data-testid={`view-lead-${l.id}`}>View</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-display">{l.name} · <span className="uppercase text-xs font-mono text-slate-500">{l.type} lead</span></DialogTitle>
                </DialogHeader>
                <pre className="text-xs whitespace-pre-wrap font-mono bg-slate-50 border border-slate-200 p-4 max-h-96 overflow-auto">{JSON.stringify(l.payload, null, 2)}</pre>
              </DialogContent>
            </Dialog>
            <Button size="sm" variant="ghost" onClick={() => onDelete(l.id)} className="rounded-none h-9 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-lead-${l.id}`}>
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Summary({ lead }) {
  const p = lead.payload || {};
  if (lead.type === "contact") return <span className="line-clamp-2">{p.service_type}: {p.message}</span>;
  if (lead.type === "quote") return <span className="line-clamp-2"><b>{(p.services || []).join(", ")}</b> · {p.project_size} · {p.budget} · {p.timeline}</span>;
  if (lead.type === "assessment") return <span className="line-clamp-2">{p.service_type} · {p.preferred_date} ({p.preferred_time})</span>;
  return null;
}
